import { motion } from 'motion/react'
import { mq, pxToRem, sizes } from 'src/theme'
import { Separator } from 'src/theme/styled'
import styled from 'styled-components'

import BlockTitle from '../BlockTitle'
import Icon from '../Icon'
import Picture from '../Picture'
import Transcript from '../Transcript'
import VideoYoutube from '../VideoYoutube'

export const Container = styled.section<{ $color?: string }>`
  --videopush-color: ${({ $color }) => $color};
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(52)};
  &:not(:first-child) {
    margin-block-start: var(--g_spacing__blocks);
  }
  @media ${mq(sizes.desktop)} {
    gap: ${pxToRem(144)};
  }
`
export const _BlockTitle = styled(BlockTitle)`
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  @media ${mq(sizes.desktop)} {
    max-width: calc(100% - ${pxToRem(32)} * 2);
  }
`

export const Wrapper = styled(motion.div)`
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(24)};

  @media ${mq(sizes.desktop)} {
    gap: ${pxToRem(40)};
  }
`
export const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: [video] 1fr [thumbnails] auto;
  width: 100%;
  margin-inline: auto;
  // single video
  ${Container}[data-single='true'] & {
    display: flex;
    flex-flow: column;
    align-items: stretch;
    max-width: calc(100% - ${pxToRem(16)} * 2);
  }
  @media ${mq(sizes.tablet)} {
    max-width: calc(100% - ${pxToRem(16)} * 2);
  }
  @media ${mq(sizes.desktop)} {
    grid-template-columns:
      [video] auto
      [content] calc(((100% - ${pxToRem(32)} * 2) / 3 * 2) + ${pxToRem(32)})
      [spacer] ${pxToRem(16)};
    grid-template-rows:
      [video] auto
      [spacer] ${pxToRem(166)}
      [content] auto;
    &,
    ${Container}[data-single='true'] & {
      max-width: calc(100% - ${pxToRem(32)} * 2);
    }
  }
`
export const Video = styled.div`
  position: relative;
  z-index: 2;
  background: ${({ theme }) => theme.colors.black};
  @media ${mq(sizes.desktop)} {
    grid-column: video / span 2;
    grid-row: video / span 2;
  }
`
export const _VideoYoutube = styled(VideoYoutube)``

// ONLY WHEN MULTIPLE VIDEOS

export const Thumbnails = styled.div`
  position: relative;
  z-index: 2;
  grid-row: content / span 1;
  grid-column: 1 / span 1;
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(32)};
  padding-block: ${pxToRem(24)} ${pxToRem(40)};
  padding-inline: ${pxToRem(16)};
  overflow: hidden;
  @media ${mq(sizes.desktop)} {
    grid-column: content / span 2;
    grid-row: content / span 1;
  }
  @media ${mq(sizes.big)} {
    padding-block: ${pxToRem(40)} ${pxToRem(40)};
    padding-inline: ${pxToRem(32)};
    gap: ${pxToRem(40)};
  }
`
export const ThumbnailsTitle = styled.h3`
  ${({ theme }) => theme.textStyles.m375_h2}
  text-wrap: balance;
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.d1440_h2}
  }
`
export const _Separator = styled(Separator)`
  position: relative;
  z-index: 2;
`
export const Tab = styled.div`
  display: flex;
  gap: ${pxToRem(16)};
  flex-flow: column;
  cursor: pointer;
  @media ${mq(sizes.desktop)} {
    gap: ${pxToRem(24)};
  }
`
export const Thumbnail = styled.div`
  position: relative;
  aspect-ratio: 640 / 360;
  width: 100%;
  background: rgba(0, 0, 0, 0.1);
`
export const ThumbnailPlay = styled.span`
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  width: ${pxToRem(32)};
  background: var(--videopush-color);
  transition: scale 0.3s ease-in-out;
  transform-origin: left bottom;
  ${Tab}:hover &,
  ${Tab}:focus & {
    scale: 1.25;
  }
`
export const ThumbnailIcon = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
`
export const ThumbnailPicture = styled(Picture)`
  width: 100%;
  height: 100%;
  & img {
    height: 100%;
    object-fit: cover;
  }
`
export const ThumbnailTitle = styled.p`
  ${({ theme }) => theme.textStyles.m375_h4}
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: opacity 0.2s ease-in-out;
  ${Tab}:not([aria-selected='true']) & {
    opacity: 0.7;
  }
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.d1440_h4}
  }
`
export const Decoration = styled.div`
  position: relative;
  z-index: 1;
  background: var(--videopush-color);
  grid-row: content / span 1;
  grid-column: 1 / span 1;
  @media ${mq(sizes.desktop)} {
    grid-column: content / span 2;
    grid-row: spacer / span 2;
  }
`
export const _Transcript = styled(Transcript)`
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  @media ${mq(sizes.desktop)} {
    max-width: calc((100% - ${pxToRem(32)} * 2) / 3 * 2);
    margin-inline: auto ${pxToRem(32)};
  }
`

// 💩 Slider has been moved directly into the VideoPush component by convenience
export const Slider = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(32)};
`
export const Keen = styled.div``
export const Slide = styled.div``
export const SliderNav = styled.div`
  display: flex;
  align-items: center;
  .keen-slider[data-keen-slider-disabled] ~ & {
    display: none;
  }
  @media ${mq(sizes.desktop)} {
    display: flex;
    .keen-slider[data-keen-slider-disabled] ~ & {
      display: none;
    }
  }
`
export const SliderDots = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-flow: row nowrap;
  gap: ${pxToRem(8)};
  @media ${mq(sizes.desktop)} {
    display: none;
  }
`
export const SliderDot = styled.button`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
  height: ${pxToRem(2)};
  background: ${({ theme }) => theme.colors.black};
  opacity: 0.3;
  &[aria-current='true'] {
    width: ${pxToRem(32)};
    opacity: 1;
  }
  & span {
    ${({ theme }) => theme.textStyles.accessibleHiddenText}
  }
`
export const SliderArrows = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${pxToRem(16)};
  margin-inline-start: auto;
  @media ${mq(sizes.desktop)} {
    margin-inline-start: 0;
  }
`
export const SliderArrow = styled.button`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(32)};
  background: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  &[disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`
export const SliderArrowIcon = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(24)};
`
export const SliderNumerotation = styled.span`
  ${({ theme }) => theme.textStyles.d1440_button}
  display: none;
  @media ${mq(sizes.desktop)} {
    display: flex;
  }
`
